import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import * as customerSpdActionCreators from '@makemydeal/dr-dash-store/actions/CustomerSpdActionCreators';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as customerSpdUtils from '@makemydeal/dr-dash-store/utils/customerSpdUtils';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';

import { dealLockHooks, useContractFlowAdapter } from '../hooks';
import { PhoneInput } from '@interstate/components/PhoneInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { DataAttributes } from '@interstate/components/CommonTypes';
import { Button } from '@interstate/components/Button';
import { TextInput } from '@interstate/components/TextInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { Typography } from '@interstate/components/Typography';
import { NameFieldsWrapper, CustomerCardFooter, LoadingIndicatorWrapper } from './CustomerCard.styles';
import {
    TITLE_OPTIONS,
    SUFFIX_OPTIONS,
    MARITAL_STATUS_OPTIONS,
    RELATIONSHIP_TO_THE_BUYER_OPTIONS,
    CO_APPLICANT_TYPE_OPTIONS,
    BUYER,
    CO_BUYER,
    BUSINESS,
    FIELDS_TO_BE_TRIMMED
} from './constants';
import { HousingAndEmploymentInfo } from './housingAndEmploymentInfo/HousingAndEmploymentInfo';
import { BusinessInformation } from './businessInformation/BusinessInformation';
import type { CustomerCardFormValues } from './utils/CustomerCardUtils';
import {
    extractNonSpdData,
    generateTestId,
    getFormValues,
    mapFormValuesToAssociateBusinessPayload,
    mapFormValuesToCreateBusinessPayload,
    mapFormValuesToCustomerPayload
} from './utils/CustomerCardUtils';
import { DiscardChangesModal } from '@makemydeal/dr-shared-components';
import CustomerSpd from './CustomerSpd';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import { Spinner } from '@interstate/components/Spinner';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import { mergeNonEmptyFields } from '@makemydeal/dr-dash-ui-utils';
import { VIEW_MODE } from './CustomerCardEnums';
import { getMidDayUTCTime } from '../utils/formatUtils';
import { DATE_FORMAT } from '../constants';
import { CustomerAddressesInterstate } from './customerAddresses/CustomerAddresses.interstate';
import { get, isEqual } from 'lodash';
import type { AddressType } from './customerAddresses/CustomerAddressTypes';
import { useFormValidationWithState } from '@makemydeal/dr-shared-ui-utils';
import { customerValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/customerValidationConfig';
import { CustomerCardProvider } from './CustomerCardProvider';

const piiInputDataAttr: DataAttributes & { className: string } = { className: 'pii' };
const piiSlotProps = { textInput: { dataAttr: piiInputDataAttr } };

interface CustomerCardProps {
    customer: typeof BUYER | typeof CO_BUYER;
    onCancel?: () => any;
    onApply?: (formValues: ReturnType<typeof getFormValues>) => any;
    hideCancel?: boolean;
    hideApply?: boolean;
    disableDiscardModal?: boolean;
    onValidationChange?: (isValid: boolean, formChanged: boolean) => void;
    enableSpd?: boolean;
    viewMode?: VIEW_MODE;
    onContractingErrorsAlert?: (hasErrors: boolean) => void;
    buyerBusinessConversionDraft?: CustomerCardFormValues | null;
}

export interface CustomerCardRef {
    apply: () => void;
    cancel: () => void;
}

const CustomerCard = forwardRef<CustomerCardRef, CustomerCardProps>(
    (
        {
            customer,
            onCancel,
            onApply,
            hideCancel = false,
            hideApply = false,
            disableDiscardModal = false,
            onValidationChange,
            enableSpd = false,
            viewMode = VIEW_MODE.FULL,
            onContractingErrorsAlert,
            buyerBusinessConversionDraft = null
        },
        ref
    ) => {
        const dispatch = useDispatch();
        const [showDiscardModal, setShowDiscardModal] = useState(false);
        const buyer = useSelector(offerReduxSelectors.getShopperInfo);
        const coBuyer = useSelector(offerReduxSelectors.getCoBuyerInfo) || {};
        const customerData = customer === BUYER ? buyer : coBuyer;
        const dealershipId = useSelector(offerReduxSelectors.getCommonOrgId);

        const buyerCommonConsumerId = useSelector(offerReduxSelectors.getShopperCommonConsumerId);
        const coBuyerCommonConsumerId = useSelector(offerReduxSelectors.getCoBuyerCommonConsumerId);
        const commonConsumerId = customer === BUYER ? buyerCommonConsumerId : coBuyerCommonConsumerId;
        const isCustomerSPDEnabled = useSelector(dealerSelectors.isSpdManagementEnabled);
        const checkSpdData = useSelector(customerSpdSelectors.getCheckCustomerSpdResult(commonConsumerId));
        const checkSpdError = useSelector(customerSpdSelectors.getCheckCustomerSpdError());
        const customerSpdData = useSelector((state: StateTree) => customerSpdSelectors.getCustomerSpdData(state, commonConsumerId));
        const [previousDateOfBirth, setPreviousDateOfBirth] = useState<string | undefined>();
        const isEnhancedCustomerCardEnabled = useSelector(featureToggleSelectors.isEnhancedCustomerCardEnabled);
        const isOptionalDesking = useSelector(dealModeSelectors.isOptionalDeskingMode);
        const isLocked = dealLockHooks.useIsDealLocked();
        const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
        const isCheckCustomerSpdLoading = useSelector(customerSpdSelectors.getCheckCustomerSpdLoading);
        const hasExistingCustomerErrors = useSelector(dealScreenErrorSelectors.getEntityErrorCount(customer)) > 0;
        const isCreditAppFieldsCoverageEnabled = useSelector(featureToggleSelectors.isCreditAppFieldsCoverageEnabled);
        const enableCorrectPhoneHandling = useSelector(featureToggleSelectors.isCorrectPhoneHandlingEnabled);

        const isOptionalDeskingActive = isOptionalDesking && !isContractingContext;
        const requiredLabel = (label: string) => (isOptionalDeskingActive ? `${label} (required)` : label);
        const isBuyerBusinessConversionFlow = customer === BUYER && !!buyerBusinessConversionDraft;
        const businessNameInputContainerRef = useRef<HTMLDivElement | null>(null);

        const customerInitialValues = useMemo(() => {
            const baseValues = getFormValues(customerData, customerSpdData, enableCorrectPhoneHandling);

            if (!isBuyerBusinessConversionFlow) {
                return baseValues;
            }

            return buyerBusinessConversionDraft;
        }, [
            customerData,
            customerSpdData,
            isBuyerBusinessConversionFlow,
            buyerBusinessConversionDraft,
            enableCorrectPhoneHandling
        ]);

        const initialFormValues = useRef(customerInitialValues);
        const {
            engine: customerBaseValidator,
            formErrors: customerFormErrors,
            formValues
        } = useFormValidationWithState(
            {
                initialConfig: customerValidationConfig,
                initialValues: customerInitialValues,
                commitConfig: {
                    keyStrategy: 'path',
                    onCommit: (errorFlags) => {
                        dispatch(dealScreenErrorActionCreators.setErrorFields(customer, errorFlags));
                    }
                }
            },
            { channel: 'inline' }
        );
        const { engine: customerValidator, hasContractErrors } = useContractFlowAdapter(customerBaseValidator, customerFormErrors);

        const editableBusinessBuyer = useMemo(() => {
            return customer === BUYER && (buyer.customerType === BUSINESS || isBuyerBusinessConversionFlow);
        }, [customer, buyer.customerType, isBuyerBusinessConversionFlow]);

        // Keep custom context values in sync and available before validations run.
        useEffect(() => {
            customerValidator.ctx.entity = 'customer';
            customerValidator.ctx.customerRole = customer;
            customerValidator.ctx.customerType = isBuyerBusinessConversionFlow ? BUSINESS : customerData.customerType;
            customerValidator.ctx.otherEmail = customer === BUYER ? coBuyer.email : buyer.email;
            // When contracting context is active, bypass OptionalDesking so Required errors can be highlighted
            customerValidator.ctx.dealMode = isOptionalDeskingActive ? 'OptionalDesking' : undefined;
        }, [
            customerValidator,
            customer,
            buyer.email,
            coBuyer.email,
            isOptionalDeskingActive,
            customerData.customerType,
            isBuyerBusinessConversionFlow
        ]);
        const customerContractingFields: string[] = useMemo(
            () => customerValidator.getFormFields({ flow: 'contract', keyStrategy: 'path' }),
            [customerValidator]
        );

        const handleInputChange = (field: string, value: string) => {
            const trimmedValue = FIELDS_TO_BE_TRIMMED.includes(field) ? value.trim() : value;
            // Persist value into validator ctx and validate the changed field
            customerValidator.setValue(field, trimmedValue, { validate: true });
        };

        const handleNumberInputChange = (field: string, value: string) => {
            const numericValue = value !== '' ? Number(value) : undefined;
            customerValidator.setValue(field, numericValue, { validate: true });
        };

        const handleBusinessNameChange = (value: string) => {
            const trimmedValue = value.trim();
            customerValidator.setValue('business.legalName', trimmedValue, { validate: true });
        };

        const handlePhoneInputChange = (
            field: 'phone' | 'cellPhone' | 'homePhone' | 'workPhone',
            e: InterstateOnChangeEvent<string>
        ) => {
            // Keep validator ctx.values in sync (Interstate handles validation visuals).
            customerValidator.setValue(field, e.target.value, { validate: false });

            // Keep Interstate validation; record result in engine error state
            const phonePrefix = {
                phone: 'Cell Phone',
                cellPhone: 'Cell Phone',
                homePhone: 'Home Phone',
                workPhone: 'Work Phone'
            };
            const isValid = e.isValid as boolean;
            customerValidator.setError(field, isValid, isValid ? '' : `${phonePrefix[field]} is invalid`);
        };

        const handleAddressFieldChange = (addressType: AddressType, field: string, value: string) => {
            if (addressType === 'primary') {
                customerValidator.setValue(field, value, { validate: true });
            } else {
                customerValidator.setValue(`billingAddress.${field}`, value, { validate: true });
            }
        };

        const handleDatePickerChange = (fieldName: string, event: InterstateOnChangeEvent<DatePickerEventValue>) => {
            const value = event.target.value;
            let dateValue = value.dateValue ? value.dateValue : '';

            // Convert SPD date fields to UTC noon
            if (dateValue && (fieldName === 'driversLicenseExpirationDate' || fieldName === 'dateOfBirth')) {
                dateValue = getMidDayUTCTime(dateValue, DATE_FORMAT) || dateValue;
            }

            handleInputChange(fieldName, dateValue);
        };

        const hasFormChanged = (formValues: any, initialFormValues: any) => {
            return Object.keys(formValues).some((key) => {
                const currentValue = formValues[key as keyof typeof formValues];
                const initialValue = initialFormValues[key as keyof typeof initialFormValues];

                if (key === 'business' && !editableBusinessBuyer) {
                    return false;
                }

                if (key === 'phone') {
                    const normalizedCurrentValue = currentValue?.replace(/\D/g, '');
                    const normalizedInitialValue = initialValue?.replace(/\D/g, '');
                    return normalizedCurrentValue !== normalizedInitialValue;
                }

                if (key === 'billingAddress') {
                    if (Boolean(currentValue) !== Boolean(initialValue)) return true;

                    if (currentValue && initialValue) {
                        return Object.keys(currentValue).some((subKey) => {
                            const currentSubValue = currentValue[subKey as keyof typeof currentValue];
                            const initialSubValue = initialValue[subKey as keyof typeof initialValue];

                            return currentSubValue !== initialSubValue;
                        });
                    }

                    return false;
                }

                return !isEqual(currentValue ?? '', initialValue ?? '');
            });
        };

        const handleDatePickerBlur = (fieldName: string, event: InterstateOnChangeEvent<DatePickerEventValue>) => {
            const value = event.target.value;
            const dateValue = value.dateValue ? value.dateValue : '';

            if (fieldName === 'dateOfBirth') {
                if (!dateValue && previousDateOfBirth) {
                    handleInputChange(fieldName, previousDateOfBirth);
                    return;
                }
            }
        };

        // TODO: Why are we checking REQUIRED here again?
        // Handle the scenario where we don't run validations on init
        // Should we force a run on init or apply instead?
        const isApplyButtonDisabled = (): boolean => {
            const currentValues = customerValidator.ctx.values as any;
            // Use new validator's form-level error flag only
            const hasErrors = customerValidator.getHasFormErrors({ channel: 'inline', refresh: false });
            const formChanged = hasFormChanged(currentValues, initialFormValues.current);

            const requiredFields = isOptionalDeskingActive ? [] : ['firstName', 'lastName', 'email', 'zip'];

            if (currentValues.billingAddress) {
                requiredFields.push('billingAddress.zip');
            }

            const requiredFieldsFilled = requiredFields.every((field) => Boolean(get(currentValues, field)));

            // If requiredFieldsFilled is true, ignore the formChanged requirement
            // This allows prefilled forms with all required fields to be considered valid
            if (requiredFieldsFilled && !hasErrors) {
                return false;
            }

            return !formChanged || hasErrors || !requiredFieldsFilled;
        };

        // Separate check for business name in new flow: disables Apply without triggering the validation banner
        const isBusinessNameMissing =
            isBuyerBusinessConversionFlow && (formValues.business ? !formValues.business.legalName : true);

        const applyCustomerChanges = async () => {
            if (!isApplyButtonDisabled() && !isBusinessNameMissing) {
                const currentValues = customerValidator.ctx.values as CustomerCardFormValues;
                const isBuyer = customer === BUYER;
                const consumerId = isBuyer ? buyerCommonConsumerId : coBuyerCommonConsumerId;
                const customerPayload = mapFormValuesToCustomerPayload(
                    currentValues,
                    consumerId,
                    dealershipId,
                    customerData,
                    enableCorrectPhoneHandling
                );
                let syncCommonConsumerId = commonConsumerId;

                if (isBuyerBusinessConversionFlow) {
                    const selectedBusinessEntityId = currentValues.business ? currentValues.business.businessEntityId : '';
                    const isExistingBusinessFlow = !!selectedBusinessEntityId;

                    let businessResult;
                    if (isExistingBusinessFlow) {
                        const associateResult = await dispatch(
                            editCustomerActionCreators.associateBusiness(
                                mapFormValuesToAssociateBusinessPayload(currentValues, enableCorrectPhoneHandling)
                            )
                        );
                        if (!associateResult?.ok) {
                            return;
                        }
                        businessResult = associateResult.data || {};
                    } else {
                        const createBusinessResult = await dispatch(
                            editCustomerActionCreators.createBusinessRSAA(
                                mapFormValuesToCreateBusinessPayload(currentValues, enableCorrectPhoneHandling)
                            )
                        );
                        if (!createBusinessResult?.ok) {
                            return;
                        }
                        businessResult = createBusinessResult.data || {};
                    }

                    customerPayload.customerType = BUSINESS;
                    customerPayload.consumerId = businessResult.commonConsumerId;
                    (customerPayload.business as { businessEntityId: string }).businessEntityId = businessResult.businessEntityId;

                    syncCommonConsumerId = businessResult.commonConsumerId;
                    dispatch(offerReduxActionCreators.businessConversionCompleted(syncCommonConsumerId));
                }

                const allowEmptyValues = true;
                const dateOfBirth = currentValues.dateOfBirth || previousDateOfBirth;
                if (isCustomerSPDEnabled && dateOfBirth && !isBuyerBusinessConversionFlow) {
                    customerPayload.dateOfBirth = dateOfBirth;
                }
                const action = isBuyer
                    ? offerReduxActionCreators.updatedShopperLocationFromConsumer(
                          buyer,
                          customerPayload,
                          allowEmptyValues,
                          isOptionalDesking
                      )
                    : offerReduxActionCreators.updateCoBuyer(customerPayload, allowEmptyValues, isOptionalDesking);
                dispatch(action);
                const nonSpdData = extractNonSpdData(currentValues);

                if (hasFormChanged(nonSpdData, initialFormValues.current)) {
                    if (syncCommonConsumerId) dispatch(editCustomerActionCreators.setCustomerToSync(syncCommonConsumerId));
                    dispatch(customerSpdActionCreators.applyCustomerCardNonSpdChanges());
                }
                if (isCustomerSPDEnabled && ((syncCommonConsumerId && checkSpdData) || isBuyerBusinessConversionFlow)) {
                    const updatedCustomerSpdData = {
                        driversLicenseNumber: currentValues.driversLicenseNumber,
                        driversLicenseState: currentValues.driversLicenseState,
                        driversLicenseExpirationDate: currentValues.driversLicenseExpirationDate,
                        dateOfBirth,
                        socialSecurityNumber: currentValues.socialSecurityNumber,
                        bankAccountNumber: currentValues.bankAccountNumber
                    };

                    const hasSpdDataChanged = customerSpdUtils.hasDataChanged(
                        updatedCustomerSpdData,
                        isBuyerBusinessConversionFlow ? {} : customerSpdData
                    );
                    if (hasSpdDataChanged) {
                        dispatch(customerSpdActionCreators.applyCustomerCardSpdChanges());
                        dispatch(customerSpdActionCreators.updateCustomerSpd(syncCommonConsumerId, updatedCustomerSpdData));
                    }
                }
                customerValidator.clearErrors();
                customerValidator.commit(customerContractingFields);

                if (isContractingContext) {
                    dispatch(contractingActionCreators.refreshContractValidationErrors());
                }

                onApply && onApply(currentValues);
            }
        };

        const cancelCustomerChanges = () => {
            const currentValues = customerValidator.ctx.values as any;
            const formChanged = hasFormChanged(currentValues, initialFormValues.current);
            if (formChanged) {
                setShowDiscardModal(true);
            } else {
                customerValidator.clearErrors();
                onCancel?.();
            }
        };

        const handleDiscardChanges = () => {
            setShowDiscardModal(false);
            customerValidator.clearErrors();
            onCancel?.();
        };

        const handleGoBack = () => {
            setShowDiscardModal(false);
        };

        useImperativeHandle(ref, () => ({
            cancel: cancelCustomerChanges,
            apply: applyCustomerChanges
        }));

        useEffect(() => {
            const baseFormValues = getFormValues(customerData, customerSpdData, enableCorrectPhoneHandling);
            const newFormValues = isBuyerBusinessConversionFlow ? buyerBusinessConversionDraft : baseFormValues;
            // For business conversion flow, keep the original buyer data as the baseline
            // so that the draft values are detected as "changed" and Apply is enabled.
            initialFormValues.current = isBuyerBusinessConversionFlow ? baseFormValues : newFormValues;

            customerValidator.setValues(newFormValues as any, { mode: 'replace' });

            if (newFormValues.dateOfBirth) {
                setPreviousDateOfBirth(newFormValues.dateOfBirth);
            }

            checkAndUpdateFormValidity();
        }, [customer, isBuyerBusinessConversionFlow, buyerBusinessConversionDraft]);

        useEffect(() => {
            if (!isBuyerBusinessConversionFlow) {
                return;
            }

            const businessNameInput = businessNameInputContainerRef.current?.querySelector('input') as HTMLInputElement | null;
            businessNameInput?.focus?.();
        }, [isBuyerBusinessConversionFlow]);

        const checkAndUpdateFormValidity = useCallback(() => {
            const isValid = !isApplyButtonDisabled();
            const currentValues = customerValidator.ctx.values as any;
            const formChanged = hasFormChanged(currentValues, initialFormValues.current);

            if (onValidationChange) {
                onValidationChange(isValid, formChanged);
            }
        }, [customerValidator, onValidationChange]);

        useEffect(() => {
            checkAndUpdateFormValidity();
        }, [formValues, customerFormErrors, onValidationChange]);

        useEffect(() => {
            if (isCustomerSPDEnabled && enableSpd && !checkSpdData && !isCheckCustomerSpdLoading) {
                dispatch(customerSpdActionCreators.checkCustomerSpdRSAA(commonConsumerId));
            }

            checkAndUpdateFormValidity();
        }, []);

        useEffect(() => {
            if (isBuyerBusinessConversionFlow) {
                return;
            }

            if (isCustomerSPDEnabled && customerSpdData) {
                const updatedFormValues = mergeNonEmptyFields(customerSpdData, formValues) as CustomerCardFormValues;
                initialFormValues.current = mergeNonEmptyFields(customerSpdData, initialFormValues.current);

                customerValidator.setValues(updatedFormValues as any, { mode: 'replace' });

                if (customerSpdData.dateOfBirth) {
                    setPreviousDateOfBirth(customerSpdData.dateOfBirth);
                }

                checkAndUpdateFormValidity();
            }
        }, [customerSpdData, isBuyerBusinessConversionFlow]);

        // Trigger contracting validations via engine when dynamic validations are enabled and contract has errors.
        useEffect(() => {
            if (isContractingContext) {
                // Customer card needs to show both default + contract validations
                customerValidator.validateAll();
            }
        }, [isContractingContext]);

        // Trigger validation when flyout opens and there are existing errors from save validation.
        // This ensures errors are immediately shown when the user opens the flyout after a save attempt.
        useEffect(() => {
            if (hasExistingCustomerErrors && !isContractingContext) {
                customerValidator.validateAll();
            }
        }, [hasExistingCustomerErrors, isContractingContext]);

        // Control GC alert banner on the top of customer card
        // Show when there are contracting errors and hide when errors are fixed
        useEffect(() => {
            onContractingErrorsAlert?.(hasContractErrors);
        }, [hasContractErrors]);

        const renderFirstNameField = () => {
            return (
                <TextInput
                    name="firstName"
                    hasError={Boolean(customerFormErrors.firstName.hasError)}
                    displayLabel={true}
                    label={requiredLabel('Legal First Name')}
                    value={formValues.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    errorMessage={customerFormErrors.firstName.errorMessage}
                    required={!isOptionalDeskingActive}
                    data-testid={generateTestId('first-name', viewMode)}
                    size="small"
                    disabled={isLocked}
                />
            );
        };

        const renderLastNameField = () => {
            return (
                <TextInput
                    name="lastName"
                    hasError={Boolean(customerFormErrors.lastName.hasError)}
                    displayLabel={true}
                    label={requiredLabel('Legal Last Name')}
                    value={formValues.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    errorMessage={customerFormErrors.lastName.errorMessage}
                    required={!isOptionalDeskingActive}
                    data-testid={generateTestId('last-name', viewMode)}
                    size="small"
                    disabled={isLocked}
                />
            );
        };

        const renderPhoneField = () => {
            const cellPhoneField = enableCorrectPhoneHandling ? 'cellPhone' : 'phone';
            return (
                <PhoneInput
                    name={cellPhoneField}
                    label="Cell Phone"
                    onChange={(e) => handlePhoneInputChange(cellPhoneField, e)}
                    value={formValues[cellPhoneField]}
                    data-testid={generateTestId('cell-phone', viewMode)}
                    size="sm"
                    disabled={isLocked}
                    slotProps={piiSlotProps}
                />
            );
        };

        const renderHomePhoneField = () => {
            return (
                <PhoneInput
                    name="homePhone"
                    label="Home Phone"
                    onChange={(e) => handlePhoneInputChange('homePhone', e)}
                    value={formValues.homePhone}
                    data-testid={generateTestId('home-phone', viewMode)}
                    size="sm"
                    disabled={isLocked}
                    slotProps={piiSlotProps}
                />
            );
        };

        const renderWorkPhoneField = () => {
            return (
                <PhoneInput
                    name="workPhone"
                    label="Work Phone"
                    onChange={(e) => handlePhoneInputChange('workPhone', e)}
                    value={formValues.workPhone}
                    data-testid={generateTestId('work-phone', viewMode)}
                    size="sm"
                    disabled={isLocked}
                    slotProps={piiSlotProps}
                />
            );
        };

        const renderEmailField = () => {
            return (
                <TextInput
                    name="email"
                    hasError={Boolean(customerFormErrors.email.hasError)}
                    displayLabel={true}
                    label={requiredLabel('Primary Email Address')}
                    value={formValues.email}
                    required={!isOptionalDeskingActive}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    errorMessage={customerFormErrors.email.errorMessage}
                    data-testid={generateTestId('primary-email', viewMode)}
                    slotProps={piiSlotProps}
                    size="small"
                    disabled={isLocked}
                />
            );
        };

        const renderZipField = () => {
            return (
                <TextInput
                    name="zip"
                    hasError={Boolean(customerFormErrors.zip.hasError)}
                    displayLabel={true}
                    label={requiredLabel('Postal Code')}
                    value={formValues.zip}
                    onChange={(e) => handleInputChange('zip', e.target.value)}
                    errorMessage={customerFormErrors.zip.errorMessage}
                    required={!isOptionalDeskingActive}
                    data-testid={generateTestId('postal-code', viewMode)}
                    size="small"
                    disabled={isLocked}
                />
            );
        };

        const renderSummaryView = () => {
            return (
                <>
                    <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
                        Summary
                    </Typography>
                    {isEnhancedCustomerCardEnabled ? (
                        <>
                            <NameFieldsWrapper columns={2}>
                                {renderFirstNameField()}
                                {renderLastNameField()}
                            </NameFieldsWrapper>
                            <NameFieldsWrapper columns={3}>
                                {renderPhoneField()}
                                {renderWorkPhoneField()}
                                {renderHomePhoneField()}
                            </NameFieldsWrapper>
                            <NameFieldsWrapper columns={2}>
                                {renderEmailField()}
                                {renderZipField()}
                            </NameFieldsWrapper>
                        </>
                    ) : (
                        <>
                            <NameFieldsWrapper columns={3}>
                                {renderFirstNameField()}
                                {renderLastNameField()}
                                {renderPhoneField()}
                            </NameFieldsWrapper>
                            <NameFieldsWrapper columns={2}>
                                {renderEmailField()}
                                {renderZipField()}
                            </NameFieldsWrapper>
                        </>
                    )}
                </>
            );
        };

        const renderFullView = () => {
            const titleFieldProps = {
                name: 'title',
                displayLabel: true,
                label: 'Title',
                value: formValues.title || '',
                'data-testid': 'customer-form-title',
                size: 'small' as const,
                disabled: isLocked
            };

            return (
                <>
                    {isCreditAppFieldsCoverageEnabled && customerData.customerType !== BUSINESS && (
                        <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
                            Personal Information
                        </Typography>
                    )}
                    <NameFieldsWrapper columns={isCreditAppFieldsCoverageEnabled ? 4 : 3}>
                        {isCreditAppFieldsCoverageEnabled && customer === CO_BUYER && buyer.customerType === BUSINESS && (
                            <SelectInput
                                name="participantRole"
                                displayLabel={true}
                                label="Co-Applicant Type"
                                value={formValues.participantRole || ''}
                                onChange={(e) => handleInputChange('participantRole', e.target.value || '')}
                                options={CO_APPLICANT_TYPE_OPTIONS}
                                data-testid="customer-form-co-applicant-type"
                                size="small"
                                disabled={isLocked}
                            />
                        )}

                        {renderFirstNameField()}

                        <TextInput
                            name="middleName"
                            hasError={Boolean(customerFormErrors.middleName.hasError)}
                            displayLabel={true}
                            label="Legal Middle Name"
                            value={formValues.middleName}
                            onChange={(e) => handleInputChange('middleName', e.target.value)}
                            errorMessage={customerFormErrors.middleName.errorMessage}
                            data-testid="customer-form-middle-name"
                            size="small"
                            disabled={isLocked}
                        />

                        {renderLastNameField()}

                        {editableBusinessBuyer ? (
                            <TextInput
                                {...titleFieldProps}
                                onChange={(e) => handleInputChange('title', e.target.value || '')}
                                hasError={customerFormErrors.title.hasError}
                                errorMessage={customerFormErrors.title.errorMessage}
                            />
                        ) : (
                            <SelectInput
                                {...titleFieldProps}
                                onChange={(e) => handleInputChange('title', e.target.value || '')}
                                options={TITLE_OPTIONS}
                            />
                        )}

                        <SelectInput
                            name="suffix"
                            displayLabel={true}
                            label="Suffix"
                            value={formValues.suffix || ''}
                            onChange={(e) => handleInputChange('suffix', e.target.value || '')}
                            options={SUFFIX_OPTIONS}
                            data-testid="customer-form-suffix"
                            size="small"
                            disabled={isLocked}
                        />
                        {isCreditAppFieldsCoverageEnabled && customerData.customerType !== BUSINESS && (
                            <SelectInput
                                name="maritalStatus"
                                displayLabel={true}
                                label="Marital Status"
                                value={formValues.maritalStatus || ''}
                                onChange={(e) => handleInputChange('maritalStatus', e.target.value || '')}
                                options={MARITAL_STATUS_OPTIONS}
                                data-testid="customer-form-marital-status"
                                size="small"
                                disabled={isLocked}
                            />
                        )}
                        {isCreditAppFieldsCoverageEnabled && customer !== BUYER && buyer.customerType !== BUSINESS && (
                            <SelectInput
                                name="relationshipToBuyer"
                                displayLabel={true}
                                label="Relationship to the Buyer"
                                value={formValues.relationshipToBuyer || ''}
                                onChange={(e) => handleInputChange('relationshipToBuyer', e.target.value || '')}
                                options={RELATIONSHIP_TO_THE_BUYER_OPTIONS}
                                data-testid="customer-form-relationship-to-the-buyer"
                                size="small"
                                disabled={isLocked}
                            />
                        )}
                    </NameFieldsWrapper>
                    <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
                        Phone Numbers
                    </Typography>
                    {isEnhancedCustomerCardEnabled ? (
                        <NameFieldsWrapper columns={isCreditAppFieldsCoverageEnabled ? 4 : 3}>
                            {renderPhoneField()}
                            {renderHomePhoneField()}
                            {renderWorkPhoneField()}
                        </NameFieldsWrapper>
                    ) : (
                        <NameFieldsWrapper columns={1}>{renderPhoneField()}</NameFieldsWrapper>
                    )}

                    <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
                        Emails
                    </Typography>
                    <NameFieldsWrapper columns={isCreditAppFieldsCoverageEnabled ? 4 : 2}>
                        {renderEmailField()}

                        <TextInput
                            name="emailAlternative"
                            hasError={Boolean(customerFormErrors.emailAlternative.hasError)}
                            displayLabel={true}
                            label="Secondary Email Address"
                            value={formValues.emailAlternative}
                            onChange={(e) => handleInputChange('emailAlternative', e.target.value)}
                            errorMessage={customerFormErrors.emailAlternative.errorMessage}
                            data-testid="customer-form-alternate-email"
                            slotProps={piiSlotProps}
                            size="small"
                            disabled={isLocked}
                        />
                    </NameFieldsWrapper>

                    <CustomerAddressesInterstate
                        customer={customer}
                        initialFormValues={formValues}
                        viewMode={viewMode}
                        validator={customerValidator}
                        onAddressFieldChange={handleAddressFieldChange}
                        disabled={isLocked}
                    />
                </>
            );
        };

        return isCheckCustomerSpdLoading ? (
            <LoadingIndicatorWrapper data-testid="customer-card-loading">
                <Spinner />
            </LoadingIndicatorWrapper>
        ) : (
            <CustomerCardProvider
                customerType={customer}
                initialFormValues={initialFormValues.current}
                formValues={formValues}
                customerValidator={customerValidator}
                isLocked={isLocked}
                handleInputChange={handleInputChange}
                handleNumberInputChange={handleNumberInputChange}
                customerFormErrors={customerFormErrors}
            >
                {!disableDiscardModal && (
                    <DiscardChangesModal
                        show={showDiscardModal}
                        onDiscard={handleDiscardChanges}
                        onGoBack={handleGoBack}
                        hasValidationErrors={isApplyButtonDisabled()}
                    />
                )}

                {editableBusinessBuyer && (
                    <>
                        <NameFieldsWrapper columns={1}>
                            <div ref={businessNameInputContainerRef}>
                                <TextInput
                                    name={`business-name`}
                                    hasError={Boolean(customerFormErrors.business.legalName.hasError)}
                                    displayLabel={true}
                                    label="Business Name"
                                    value={formValues.business?.legalName}
                                    onChange={(e) => handleBusinessNameChange(e.target.value)}
                                    errorMessage={customerFormErrors.business.legalName.errorMessage}
                                    required={true}
                                    data-testid="customer-form-business-name"
                                    size="small"
                                    disabled={isLocked}
                                />
                            </div>
                        </NameFieldsWrapper>

                        <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
                            Signer
                        </Typography>
                    </>
                )}

                {viewMode === VIEW_MODE.COLLAPSED ? renderSummaryView() : renderFullView()}

                {isCustomerSPDEnabled && enableSpd && !checkSpdError && (
                    <CustomerSpd
                        key={isBuyerBusinessConversionFlow ? 'spd-business' : 'spd-individual'}
                        commonConsumerId={commonConsumerId}
                        formValues={formValues}
                        formErrors={customerFormErrors}
                        isBusinessConversionFlow={isBuyerBusinessConversionFlow}
                        handleInputChange={handleInputChange}
                        handleDatePickerChange={handleDatePickerChange}
                        handleDatePickerBlur={handleDatePickerBlur}
                    />
                )}

                {viewMode !== VIEW_MODE.COLLAPSED && isCreditAppFieldsCoverageEnabled && !editableBusinessBuyer && (
                    <HousingAndEmploymentInfo />
                )}
                {viewMode !== VIEW_MODE.COLLAPSED && editableBusinessBuyer && <BusinessInformation />}

                <CustomerCardFooter>
                    {!hideCancel && (
                        <Button
                            id="customer-slideout-cancel-btn"
                            data-testid="customer-slideout-cancel-button"
                            buttonStyle="tertiary"
                            size="medium"
                            onClick={cancelCustomerChanges}
                            disabled={isLocked}
                        >
                            Cancel
                        </Button>
                    )}
                    {!hideApply && (
                        <Button
                            id="customer-slideout-apply-btn"
                            data-testid="customer-slideout-apply-button"
                            buttonStyle="primary"
                            size="medium"
                            sx={{ marginLeft: '1.143rem' }}
                            disabled={isLocked || isApplyButtonDisabled() || isBusinessNameMissing}
                            onClick={applyCustomerChanges}
                        >
                            Apply
                        </Button>
                    )}
                </CustomerCardFooter>
            </CustomerCardProvider>
        );
    }
);

export default CustomerCard;
